import {findKey} from 'lodash';
import React, {useEffect, useState} from 'react';
import {Button, Modal, Form} from '@goproblems/ui';

import {hideModal, useDispatch, useSelector} from '@utils';
import {MODAL_AI_SETTINGS} from '@utils';
import {aiSettingsSlice} from '@reducers';
import {KatagoBackend} from '@types';
import {useTranslation} from 'react-i18next';

export const NETWORK_MODEL_MAP = {
    b6: 'kata1-b6c96-s175395328-d26788732',
    b10: 'kata1-b10c128-s1141046784-d204142634',
    b15: 'kata1-b15c192-s1672170752-d466197061',
    b20: 'kata1-b20c256-s241116160-d150094945',
    b40: 'kata1-b40c256-s12860905472-d3197353276',
};

export const AiSettingsModal = ({
    name = MODAL_AI_SETTINGS,
}: {
    name?: string;
}) => {
    const {t} = useTranslation();
    const show = useSelector(s => s.modalAiSettings);
    const aiSettings = useSelector(s => s.aiSettings);
    const dispatch = useDispatch();
    const [model, setModel] = useState(aiSettings.model);
    const [backend, setBackend] = useState(aiSettings.backend);

    const handleClose = () => {
        hideModal(name);
    };

    const handleBackendChange = (backend: KatagoBackend) => {
        setBackend(backend);
    };

    const handleModelChange = (model: string) => {
        setModel(model);
    };

    const handleVisitsChange = (maxVisits: string) => {
        dispatch(
            aiSettingsSlice.actions.setAiSettings({
                ...aiSettings,
                maxVisits,
            })
        );
    };

    // Initial values for model and backend won't read from the value from localStorage.
    // So we need to listen here
    useEffect(() => {
        setModel(aiSettings.model);
        setBackend(aiSettings.backend);
    }, [aiSettings]);

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>{t('ai_settings.title')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mb-3">
                    <Form.Label>{t('ai_settings.backend')}</Form.Label>
                    <Form.Select
                        onChange={e =>
                            handleBackendChange(e.target.value as KatagoBackend)
                        }
                        value={backend}>
                        <option value="auto">auto</option>
                        <option value="cpu">cpu</option>
                        {/* <option value="wasm">wasm</option> */}
                        <option value="webgl">webgl</option>
                        <option value="webgpu">webgpu</option>
                    </Form.Select>
                    <ul className="text-sm mt-2">
                        <li>
                            <strong>auto</strong>:{' '}
                            {t('ai_settings.backend_auto')}
                        </li>
                        <li>
                            <strong>cpu</strong>:{' '}
                            {t('ai_settings.backend_cpu')}
                        </li>
                        {/* <li>
                            <strong>wasm</strong>: Leverages WebAssembly for
                            better performance and compatibility compared to
                            CPU.
                        </li> */}
                        <li>
                            <strong>webgl</strong>:{' '}
                            {t('ai_settings.backend_webgl')}
                        </li>
                        <li>
                            <strong>webgpu</strong>:{' '}
                            {t('ai_settings.backend_webgpu')}
                        </li>
                    </ul>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>{t('ai_settings.model')}</Form.Label>
                    <Form.Select
                        onChange={e => handleModelChange(e.target.value)}
                        value={model}>
                        <option value="kata1-b6c96-s175395328-d26788732">
                            b6(4MB)
                        </option>
                        <option value="kata1-b10c128-s1141046784-d204142634">
                            b10(11.5MB)
                        </option>
                        <option value="kata1-b15c192-s1672170752-d466197061">
                            b15(37.9MB)
                        </option>
                        <option value="kata1-b20c256-s241116160-d150094945">
                            b20(89.7MB)
                        </option>
                        <option value="kata1-b40c256-s12860905472-d3197353276">
                            b40(178.2MB)
                        </option>
                    </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>{t('ai_settings.max_visits')}</Form.Label>
                    <Form.Select
                        onChange={e => handleVisitsChange(e.target.value)}
                        value={aiSettings.maxVisits}>
                        <option value={100}>100</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                        <option value={2000}>2000</option>
                        <option value={5000}>5000</option>
                        <option value={-1}>{t('common.unlimited')}</option>
                    </Form.Select>
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={() => {
                        let reload = false;
                        if (
                            aiSettings.backend !== backend ||
                            aiSettings.model !== model
                        ) {
                            reload = true;
                        }
                        dispatch(
                            aiSettingsSlice.actions.setAiSettings({
                                ...aiSettings,
                                model,
                                backend,
                                network: findKey(
                                    NETWORK_MODEL_MAP,
                                    v => v === model
                                ),
                            })
                        );
                        if (reload) {
                            setTimeout(() => window.location.reload(), 1000);
                        }
                        handleClose();
                    }}>
                    {t('common.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
